import { MessageContext } from '@snapchat/snap-design-system-marketing';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AppContext } from '../../../../../AppContext';
import { createUuidV4Browser } from '../../../../../clientonly/browserCreateUuidV4';
import type { AgentChatMessageSource, ChatMessage } from '../types';
import { clearMessages, loadMessages, saveMessages } from '../utils/messagesStorage';
import {
  clearSessionId,
  isValidSessionId,
  loadSessionId,
  saveSessionId,
} from '../utils/sessionIdStorage';
import { streamAiAgentResponse } from '../utils/streamAiAgentResponse';

export interface UseAiAgentResult {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  messages: ChatMessage[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  isStreaming: boolean;
  sendMessage: (text: string) => void;
  resendLastMessage: () => void;
}

export const useAiAgent = (): UseAiAgentResult => {
  const { cookieManager } = useContext(AppContext);
  const { formatMessage } = useContext(MessageContext);
  const errorMessage = formatMessage({
    id: 'aiAgentErrorMessage',
    defaultMessage: 'Sorry, there was an error. Please try again.',
  });

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasHydratedMessages, setHasHydratedMessages] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const sessionIdRef = useRef<string | undefined>(undefined);

  /* Resolve the session ID on demand: prefer the in-memory ref, then a persisted value, then
   * derive from Blizzard cookies. Done lazily at send-time (rather than on mount) because the
   * Blizzard SDK writes its cookies asynchronously — it may not have written them yet when the
   * hook first mounts, but by the time the user sends a message they are almost always present.
   */
  const resolveSessionId = (): string | undefined => {
    if (sessionIdRef.current) return sessionIdRef.current;

    const persistedSessionId = loadSessionId();

    if (persistedSessionId) {
      sessionIdRef.current = persistedSessionId;
      return persistedSessionId;
    }

    const blizzardClientId = cookieManager.getCookie('blizzard_client_id') ?? '';
    const blizzardWebSessionId = cookieManager.getCookie('blizzard_web_session_id') ?? '';

    if (!blizzardClientId || !blizzardWebSessionId) return undefined;

    const candidateSessionId = `${blizzardClientId}|${blizzardWebSessionId}`;

    if (!isValidSessionId(candidateSessionId)) return undefined;

    sessionIdRef.current = candidateSessionId;
    saveSessionId(candidateSessionId);
    return candidateSessionId;
  };

  /* Hydrate persisted chat history after mount, but only when BOTH a session ID and messages
   * are present in storage. If either is missing we treat the persisted state as inconsistent
   * and reset both to pristine, because:
   *   - Messages without a session ID → the server has no memory of them.
   *   - Session ID without messages → the server has a conversation the user doesn't see.
   * Either way, sending a message with a mismatched pair would put the UI and server out of
   * sync.
   *
   * Hydration is done in an effect (not render) to avoid reading storage during render, which
   * would produce a server/client markup mismatch under SSR.
   */
  useEffect(() => {
    const persistedSessionId = loadSessionId();
    const persistedMessages = loadMessages();

    if (!persistedSessionId || persistedMessages.length === 0) {
      clearSessionId();
      clearMessages();
      setHasHydratedMessages(true);
      return;
    }

    /* Pin the id that matches the hydrated messages into the ref. If session storage is
     * cleared between now and the first send (other tab, eviction, etc.), resolveSessionId
     * will still return this id instead of falling back to cookies and producing one that
     * doesn't match the conversation on screen.
     */
    sessionIdRef.current = persistedSessionId;

    setMessages(persistedMessages);
    setHasHydratedMessages(true);
  }, []);

  /* Persist the chat history at streaming boundaries — after hydration, after a user send, and
   * once each streamed agent response completes. Skipping writes while streaming avoids
   * serializing and writing the entire history on every chunk. Also skips writes before hydration
   * to avoid writing the initial empty state before hydration has had a chance to run.
   */
  useEffect(() => {
    if (!hasHydratedMessages) return;

    /* React could batch the user message append with setIsStreaming(true), so `isStreaming` alone
     * would skip it. The role check keeps user sends from being dropped.
     */
    const lastMessage = messages[messages.length - 1];
    const isLastMessageFromAgent = lastMessage?.role === 'agent';

    if (isStreaming && isLastMessageFromAgent) return;

    saveMessages(messages);
  }, [hasHydratedMessages, messages, isStreaming]);

  const sendMessage = useCallback(
    (message: string) => {
      if (!message.trim() || isLoading || isStreaming) return;

      setMessages(prev => [
        ...prev,
        { id: createUuidV4Browser(), role: 'user', content: message.trim() },
      ]);

      // Clear the input value
      setInputValue('');

      // Set the loading state to true
      setIsLoading(true);
      setIsStreaming(true);

      // Create a new message ID for the agent response so that it can be updated as the message is streamed
      const agentMessageId = createUuidV4Browser();

      // Track if this is the first chunk of the message to avoid updating the message ID multiple times
      let isFirstChunk = true;

      // Accumulate the message as it is streamed from the API
      streamAiAgentResponse({
        message,
        sessionId: resolveSessionId(),
        onChunk: (accumulatedResponse, responseSources) => {
          // Convert response sources to chat message sources
          const sources: AgentChatMessageSource[] =
            responseSources?.map(source => ({ title: source.title, url: source.url })) ?? [];

          // If this is the first chunk, add the message to the conversation history
          if (isFirstChunk) {
            setMessages(prev => [
              ...prev,
              { id: agentMessageId, role: 'agent', content: accumulatedResponse, sources },
            ]);
            isFirstChunk = false;

            // Turn off loading state after the first chunk is received, as streaming state will
            // remain true until the response is complete
            setIsLoading(false);
          }
          // If this is not the first chunk, update the message in the conversation history
          else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === agentMessageId
                  ? {
                      id: agentMessageId,
                      role: 'agent' as const,
                      content: accumulatedResponse,
                      sources,
                    }
                  : msg
              )
            );
          }
        },
      })
        .then(result => {
          /* If we sent a session ID in the request, we will receive that same session ID back.
           * If we didn't send a session ID, the API will generate a new one which we should store
           * and use for future requests.
           */
          if (result.sessionId && isValidSessionId(result.sessionId)) {
            sessionIdRef.current = result.sessionId;
            saveSessionId(result.sessionId);
          }
        })
        .catch(error => {
          console.error(error);

          if (isFirstChunk) {
            setMessages(prev => [
              ...prev,
              { id: agentMessageId, role: 'error', content: errorMessage },
            ]);
            isFirstChunk = false;
          } else {
            setMessages(prev =>
              prev.map(msg =>
                msg.id === agentMessageId
                  ? { id: agentMessageId, role: 'error', content: errorMessage }
                  : msg
              )
            );
          }
        })
        .finally(() => {
          setIsLoading(false);
          setIsStreaming(false);
        });
    },
    /* resolveSessionId is intentionally omitted: it only closes over stable values
     * (sessionIdRef and cookieManager from context), so the captured closure is
     * always correct. Listing it would re-create sendMessage every render.
     * eslint-disable-next-line react-hooks/exhaustive-deps
     */
    [isLoading, isStreaming, errorMessage]
  );

  const resendLastMessage = useCallback(() => {
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');

    if (lastUserMessage) {
      sendMessage(lastUserMessage.content);
    }
  }, [messages, sendMessage]);

  return {
    isOpen,
    setIsOpen,
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isStreaming,
    sendMessage,
    resendLastMessage,
  };
};
